<!--
  Shared membership card — member portal (2026-08-07).
  Structure ported from atenza-front .../member/pages/membership-card/, with the
  MEDHEALTH branding + WeMAS scheme palettes of the funder card
  (portals/funder/dependents/view-membership-card).

  Atenza's #noPalette branch is intentionally absent: cardSchemeKey() here can
  never return null (it falls back to 'default'), so that branch would be dead
  code. See the class docblock.
-->
<ng-container *ngIf="hasCard(); else noCard">
  <div class="membership-card"
       [class.flipped]="flipped"
       [class.compact]="compact"
       [class.auto-rotate]="autoRotate"
       [attr.data-scheme]="cardSchemeKey()"
       (click)="onCardClick()"
       [attr.role]="autoRotate ? null : 'button'"
       [attr.tabindex]="autoRotate ? null : 0"
       (keydown.enter)="onCardClick()"
       (keydown.space)="onCardClick(); $event.preventDefault()"
       [attr.aria-label]="flipped ? 'Showing card back. Click to flip to front.' : 'Showing card front. Click to flip to back.'">
    <div class="card-inner">

      <!-- ====== FRONT FACE ====== -->
      <div class="card-face card-front">
        <!--
          Decorative accent layer. The gradient id is namespaced `mmc-` so it
          cannot collide with the funder card's `accent-shine` if both ever
          render on the same page.
        -->
        <svg class="card-accent" viewBox="0 0 380 240" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="mmc-accent-shine" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="rgba(255,255,255,0.18)" />
              <stop offset="50%" stop-color="rgba(255,255,255,0.04)" />
              <stop offset="100%" stop-color="rgba(255,255,255,0.18)" />
            </linearGradient>
          </defs>
          <path d="M 380,60 Q 250,180 380,260 L 380,240 Z" fill="url(#mmc-accent-shine)" />
          <path d="M 0,0 Q 140,40 0,140 L 0,0 Z" fill="rgba(255,255,255,0.08)" />
          <circle cx="320" cy="40" r="80" fill="rgba(255,255,255,0.05)" />
        </svg>

        <div class="card-front-content">
          <div class="card-top">
            <!-- MEDHEALTH mark — same asset the funder card uses. -->
            <img class="card-logo" src="assets/images/logo-long.png" alt="MEDHEALTH" />
            <div class="card-scheme-watermark">{{ schemeName }}</div>
          </div>

          <div class="card-chips">
            <svg class="card-chip" viewBox="0 0 50 38" aria-hidden="true">
              <rect x="1" y="1" width="48" height="36" rx="5" ry="5" fill="#d4a64a" stroke="#a07b2b"
                stroke-width="0.6" />
              <path d="M 14,1 L 14,11 L 1,11 M 36,1 L 36,11 L 49,11
                       M 14,37 L 14,27 L 1,27 M 36,37 L 36,27 L 49,27
                       M 14,11 L 36,11 M 14,27 L 36,27
                       M 14,11 L 14,27 M 36,11 L 36,27" fill="none" stroke="#a07b2b" stroke-width="0.6" />
            </svg>
            <svg class="card-contactless" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M 8,7 Q 11,12 8,17" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" />
              <path d="M 12,5 Q 17,12 12,19" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" />
              <path d="M 16,3 Q 23,12 16,21" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </div>

          <div class="card-number-block">
            <div class="card-number">{{ formatCardNumber(dependent?.membership_card_number) }}</div>
            <div class="card-suffix">{{ memberSuffixLabel() }}</div>
          </div>

          <div class="card-bottom">
            <div class="card-name">
              {{ dependent?.membership_card_name_on_card || (dependent?.first_name + ' ' + dependent?.last_name) }}
            </div>
            <div class="card-expiry">
              <span class="label">VALID<br>DATE</span>
              <span class="value">{{ dependent?.membership_card_expiry_date | date: 'MM/yy' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ====== BACK FACE ====== -->
      <div class="card-face card-back">
        <div class="card-magstripe"></div>
        <div class="card-back-content">
          <div class="card-signature">
            <div class="card-signature-line">
              <span class="card-signature-text">AUTHORISED SIGNATURE</span>
              <span class="card-signature-cv">{{ dependent?.membership_card_number?.slice(-4) }}</span>
            </div>
          </div>

          <div class="card-info">
            <div class="card-info-row">
              <span class="card-info-label">SCHEME</span>
              <span class="card-info-value">{{ schemeName }}</span>
            </div>
            <div class="card-info-row">
              <span class="card-info-label">STATUS</span>
              <span class="card-info-value status-pill" [class.active]="dependent?.membership_card_status === 'ACTIVE'">
                {{ dependent?.membership_card_status }}
              </span>
            </div>
            <div class="card-info-row">
              <span class="card-info-label">ISSUED</span>
              <span class="card-info-value">{{ dependent?.membership_card_date_issued | date: 'd MMM yyyy' }}</span>
            </div>
          </div>

          <div class="card-back-footer">
            <span class="card-powered">MEDHEALTH</span>
            <span class="card-flip-hint"><i class="bi bi-arrow-clockwise"></i> Tap to flip</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="card-hint" *ngIf="!compact && !autoRotate">
    <i class="bi bi-arrow-repeat me-1"></i> Click the card to flip
  </div>
</ng-container>

<ng-template #noCard>
  <div class="mc-fallback">
    <i class="bi bi-credit-card-2-front"></i>
    Your digital card will appear here once your membership card has been issued.
  </div>
</ng-template>
